import * as React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Button,
  Text,
  Badge,
  makeStyles,
  tokens,
  Divider,
} from "@fluentui/react-components";
import {
  Dismiss24Regular,
  Circle24Regular,
  ArrowSync24Regular,
  ErrorCircle24Regular,
  CheckmarkCircle24Regular,
  Bot24Regular,
  Clock24Regular,
  Wrench24Regular,
  Chat24Regular,
  ChevronDown24Regular,
  ChevronRight24Regular,
  ChevronDown12Regular,
  ChevronRight12Regular,
  Document24Regular,
  ArrowDownload24Regular,
} from "@fluentui/react-icons";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { TaskDto } from "../../../services/tasks.service";
import { downloadClientDocumentVersion } from "../../../services/client-documents.service";
import {
  normalizeTaskStatus,
  getTaskStatusColor,
} from "../../../utils/taskStatusHelpers";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "600px",
    minWidth: "450px",
    maxHeight: "90vh",
  },
  dialogBody: {
    overflow: "hidden",
    maxHeight: "calc(90vh - 48px)",
  },
  dialogTitle: {
    flexWrap: "nowrap",
    flexShrink: 0,
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    overflow: "hidden",
    paddingBottom: tokens.spacingVerticalM,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  headerIcon: {
    color: "#0078D4",
    fontSize: tokens.fontSizeBase600,
  },
  badges: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  promptText: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    whiteSpace: "pre-wrap",
    fontFamily: "monospace",
    fontSize: tokens.fontSizeBase200,
  },
  resultContainer: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    maxHeight: "250px",
    overflowY: "auto",
  },
  resultHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  resultSuccess: {
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  resultFailed: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  responseText: {
    lineHeight: "1.5",
    "& p": {
      marginTop: 0,
      marginBottom: tokens.spacingVerticalS,
    },
    "& p:last-child": {
      marginBottom: 0,
    },
    "& ul, & ol": {
      marginTop: 0,
      marginBottom: tokens.spacingVerticalS,
      paddingLeft: tokens.spacingHorizontalL,
    },
    "& li": {
      marginBottom: tokens.spacingVerticalXS,
    },
    "& code": {
      backgroundColor: tokens.colorNeutralBackground4,
      padding: "2px 4px",
      borderRadius: tokens.borderRadiusSmall,
      fontFamily: "monospace",
      fontSize: tokens.fontSizeBase200,
    },
    "& pre": {
      backgroundColor: tokens.colorNeutralBackground4,
      padding: tokens.spacingVerticalS,
      borderRadius: tokens.borderRadiusMedium,
      overflowX: "auto",
      marginTop: 0,
      marginBottom: tokens.spacingVerticalS,
    },
    "& pre code": {
      backgroundColor: "transparent",
      padding: 0,
    },
    "& strong": {
      fontWeight: tokens.fontWeightSemibold,
    },
    "& h1, & h2, & h3, & h4": {
      marginTop: tokens.spacingVerticalS,
      marginBottom: tokens.spacingVerticalXS,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
  },
  timestamps: {
    display: "flex",
    gap: tokens.spacingHorizontalL,
  },
  timestampItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  noResult: {
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  reconciliationDetails: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  reconciliationRow: {
    display: "flex",
    justifyContent: "space-between",
  },
  // Document-output styling — summary sits at the top, then a list of
  // generated files with a download button per row. Same
  // responseText typography for the markdown summary so it looks
  // consistent with the reconciliation/freeform view.
  resultMarkdown: {
    lineHeight: "1.5",
    "& p": { marginTop: 0, marginBottom: tokens.spacingVerticalS },
    "& p:last-child": { marginBottom: 0 },
    "& ul, & ol": {
      marginTop: 0,
      marginBottom: tokens.spacingVerticalS,
      paddingLeft: tokens.spacingHorizontalL,
    },
    "& li": { marginBottom: tokens.spacingVerticalXS },
    "& strong": { fontWeight: tokens.fontWeightSemibold },
  },
  documentList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalS,
  },
  documentRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
  },
  documentRowText: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  agentActivitySection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    minHeight: 0,
    flexShrink: 1,
    overflow: "hidden",
  },
  agentActivityHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    cursor: "pointer",
    userSelect: "none",
  },
  agentActivityList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    overflowY: "auto",
    minHeight: 0,
    flexShrink: 1,
  },
  agentMessage: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  agentMessageLabel: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  toolToggleContainer: {
    minWidth: 0,
  },
  toolToggleHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXS,
    cursor: "pointer",
    userSelect: "none",
    overflow: "hidden",
    minWidth: 0,
  },
  toolTogglePreview: {
    fontFamily: "monospace",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  toolCallBlock: {
    backgroundColor: tokens.colorNeutralBackground4,
    padding: tokens.spacingVerticalXS,
    borderRadius: tokens.borderRadiusSmall,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word" as const,
    maxHeight: "200px",
    overflowY: "auto",
  },
  assistantText: {
    whiteSpace: "pre-wrap",
    fontSize: tokens.fontSizeBase300,
    lineHeight: "1.5",
  },
  // --- Error result styles (graceful failed-task display) ---
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  errorHeadline: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteRedForeground1,
  },
  errorBody: {
    color: tokens.colorNeutralForeground2,
  },
  errorTechnicalToggle: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    cursor: "pointer",
    userSelect: "none",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  errorTechnicalBlock: {
    backgroundColor: tokens.colorNeutralBackground4,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word" as const,
    fontFamily: "monospace",
    fontSize: tokens.fontSizeBase200,
  },
});

interface TaskDetailDialogProps {
  task: TaskDto | null;
  isOpen: boolean;
  onDismiss: () => void;
}

// Result type for reconciliation tasks (PascalCase to match C# API response)
interface ReconciliationTaskResult {
  TargetValue: number;
  ResultValue: number;
}

// Result type for checklist tasks (PascalCase to match C# API response)
interface ChecklistTaskResult {
  Success: boolean;
}

/**
 * Shape written by AgentService when a `document`-output-type agent
 * completes: the agent supplies `summary`; the framework auto-populates
 * `documents[]` by scanning container outputs and saving each as a
 * ClientDocument on the workbook's client. Camel-cased on the wire —
 * the framework builds the JSON via System.Text.Json.Nodes, not the
 * agent's PascalCase submit_result payload.
 */
interface DocumentResultItem {
  id: string;
  filename: string;
  mimeType: string;
  version: number;
  createdNewDocument: boolean;
}

interface DocumentTaskResult {
  summary: string;
  documents: DocumentResultItem[];
}

/**
 * Shape written by the backend's `MarkTaskIncompleteAsync` / `TryMarkFatalAsync`
 * helpers when a task fails — distinct from the per-output-type result shapes.
 */
interface ErrorTaskResult {
  success: false;
  errorMessage: string;
  processedAt?: string;
}

// Union type for all possible task results
type TaskResult = ReconciliationTaskResult | ChecklistTaskResult | DocumentTaskResult | ErrorTaskResult | null;

// Agent message type (matches backend AgentMessage)
interface AgentMessage {
  Role: string;
  Content?: string;
  ToolName?: string;
  ToolArguments?: string;
  ToolResult?: string;
}

const formatToolName = (name: string): string =>
  name.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StylesType = ReturnType<typeof useStyles>;

const CollapsibleToolBlock: React.FC<{
  content: string;
  styles: StylesType;
}> = ({ content, styles }) => {
  const typography = useTypographyStyles();
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={styles.toolToggleContainer}>
      <div className={styles.toolToggleHeader} onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown12Regular /> : <ChevronRight12Regular />}
        <span className={styles.toolTogglePreview}>
          {expanded ? "Hide" : content}
        </span>
      </div>
      {expanded && <div className={`${typography.monospace} ${styles.toolCallBlock}`}>{content}</div>}
    </div>
  );
};

const parseAgentMessages = (messagesJson?: string): AgentMessage[] => {
  if (!messagesJson) return [];
  try {
    return JSON.parse(messagesJson) as AgentMessage[];
  } catch {
    return [];
  }
};

const getStatusIcon = (status?: string): React.ComponentType => {
  const statusLower = status?.toLowerCase() || "to_do";
  switch (statusLower) {
    case "to_do":
      return Circle24Regular;
    case "in_progress":
      return ArrowSync24Regular;
    case "incomplete":
      return ErrorCircle24Regular;
    case "complete":
      return CheckmarkCircle24Regular;
    default:
      return Circle24Regular;
  }
};

const formatDateTime = (dateString?: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString();
};

const parseResult = (resultString?: string): TaskResult => {
  if (!resultString) return null;
  try {
    return JSON.parse(resultString) as TaskResult;
  } catch {
    return null;
  }
};

// Type guards for result types
const isReconciliationResult = (
  result: TaskResult,
  outputType?: string
): result is ReconciliationTaskResult => {
  return (
    outputType?.toLowerCase() === "reconciliation" &&
    result !== null &&
    "TargetValue" in result &&
    "ResultValue" in result
  );
};

const isChecklistResult = (
  result: TaskResult,
  outputType?: string
): result is ChecklistTaskResult => {
  return (
    outputType?.toLowerCase() === "checklist" &&
    result !== null &&
    ("Success" in result || "success" in result)
  );
};

/**
 * Detects the "task failed" payload (backend writes `{success: false, errorMessage, processedAt}`).
 * Checked BEFORE the output-type-specific predicates so the user sees the
 * graceful failure view regardless of which task type they ran.
 */
const isErrorResult = (result: TaskResult): result is ErrorTaskResult => {
  if (!result) return false;
  const obj = result as unknown as Record<string, unknown>;
  return obj.success === false && typeof obj.errorMessage === "string" && obj.errorMessage.length > 0;
};

/**
 * Freeform tasks have no structured result schema — the submitted JSON is just
 * `{ "result": "<text>" }`. The agent's final assistant message tells the
 * fuller story, so we render that (same approach as reconciliation).
 */
const isFreeformResult = (outputType?: string): boolean => {
  return outputType?.toLowerCase() === "freeform";
};

/**
 * Document tasks carry a `summary` string + a framework-populated
 * `documents[]` array of the ClientDocument versions that were saved on
 * the workbook's client during the run. Either field may be missing on
 * a partial result — checked defensively before rendering.
 */
const isDocumentResult = (
  result: TaskResult,
  outputType?: string
): result is DocumentTaskResult => {
  if (outputType?.toLowerCase() !== "document" || result === null) return false;
  const obj = result as unknown as Record<string, unknown>;
  return typeof obj.summary === "string" || Array.isArray(obj.documents);
};

// Component for rendering reconciliation results.
// Shows the agent's final narrative message (rendered as markdown) rather than
// raw numbers — the reconciliation task's structured TargetValue/ResultValue are
// often zero-defaulted and less useful than the agent's actual summary.
const ReconciliationResultView: React.FC<{
  agentMessages: AgentMessage[];
  styles: StylesType;
}> = ({ agentMessages, styles }) => {
  // Find the last assistant message with non-empty text content
  const lastAssistantMessage = [...agentMessages]
    .reverse()
    .find((m) => m.Role === "assistant" && m.Content && m.Content.trim().length > 0);

  if (!lastAssistantMessage?.Content) {
    return (
      <Text className={styles.noResult}>
        No summary available from the agent.
      </Text>
    );
  }

  return (
    <div className={styles.responseText}>
      <Markdown remarkPlugins={[remarkGfm]}>{lastAssistantMessage.Content}</Markdown>
    </div>
  );
};

/**
 * Renders a failed task in a way that doesn't dump a C# exception in a user's
 * face. Headline + reassurance up front; the raw error is tucked behind a
 * collapsible disclosure for the user (or whoever they forward this to) to
 * inspect if needed.
 */
const ErrorResultView: React.FC<{
  result: ErrorTaskResult;
  styles: StylesType;
}> = ({ result, styles }) => {
  const [showDetails, setShowDetails] = useState(false);
  return (
    <div className={styles.errorContainer}>
      <Text className={styles.errorHeadline}>This agent didn&rsquo;t complete</Text>
      <Text className={styles.errorBody}>
        Something went wrong while running the agent. You can try running it again — if the same problem
        keeps happening, copy the technical details below and send them to your support contact.
      </Text>
      <div
        className={styles.errorTechnicalToggle}
        onClick={() => setShowDetails((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setShowDetails((v) => !v);
        }}
      >
        {showDetails ? <ChevronDown12Regular /> : <ChevronRight12Regular />}
        <span>Technical details</span>
      </div>
      {showDetails && (
        <div className={styles.errorTechnicalBlock}>{result.errorMessage}</div>
      )}
    </div>
  );
};

/**
 * Renders a document-output-type task result: the agent's summary (as
 * markdown, since the model often adds bullet lists / notes) plus a list
 * of the generated ClientDocument versions with a download button on
 * each row. Downloads go through the existing
 * `downloadClientDocumentVersion` helper the Documents page uses, so
 * behaviour + auth line up.
 */
const DocumentResultView: React.FC<{
  result: DocumentTaskResult;
  styles: StylesType;
}> = ({ result, styles }) => {
  const typography = useTypographyStyles();
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const download = async (item: DocumentResultItem) => {
    // Track by ClientDocument id so multiple rows can be downloaded in
    // parallel without their loading indicators bleeding into each other.
    setDownloadingIds((prev) => new Set(prev).add(item.id));
    try {
      await downloadClientDocumentVersion(item.id, item.version, item.filename);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  return (
    <div className={styles.resultContainer}>
      {(!result.documents || result.documents.length === 0) ? (
        <Text className={styles.noResult}>
          The agent didn&rsquo;t save any documents on this run.
        </Text>
      ) : (
        <div className={styles.documentList}>
          <Text className={typography.sectionLabel}>Generated documents</Text>
          {result.documents.map((doc) => (
            <div key={doc.id} className={styles.documentRow}>
              <Document24Regular />
              <div className={styles.documentRowText}>
                <Text className={typography.bodySmallStrong}>{doc.filename}</Text>
                <Text className={typography.caption}>
                  Version {doc.version}
                  {doc.createdNewDocument ? " · newly created" : " · new version of existing document"}
                </Text>
              </div>
              <Button
                appearance="secondary"
                icon={<ArrowDownload24Regular />}
                onClick={() => download(doc)}
                disabled={downloadingIds.has(doc.id)}
              >
                {downloadingIds.has(doc.id) ? "Downloading..." : "Download"}
              </Button>
            </div>
          ))}
          <Text className={typography.caption}>
            Every generated document is also available in the Documents page.
          </Text>
        </div>
      )}
      {result.summary && (
        <div className={styles.resultMarkdown}>
          <Markdown remarkPlugins={[remarkGfm]}>{result.summary}</Markdown>
        </div>
      )}
    </div>
  );
};

// Component for rendering checklist results
const ChecklistResultView: React.FC<{
  result: ChecklistTaskResult;
  styles: StylesType;
}> = ({ result, styles }) => {
  // Handle both PascalCase (LLM output) and camelCase keys
  const resultObj = result as unknown as Record<string, unknown>;
  const success = resultObj.Success ?? resultObj.success;
  return (
    <div className={styles.resultHeader}>
      <Text className={success ? styles.resultSuccess : styles.resultFailed}>
        {success ? "Completed Successfully" : "Failed"}
      </Text>
    </div>
  );
};

/**
 * Drawer-style detail view for a task — prompt, status, result, and the
 * agent's tool-call transcript rendered as collapsible blocks.
 */
const TaskDetailDialog: React.FC<TaskDetailDialogProps> = ({
  task,
  isOpen,
  onDismiss,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  // At most one of Result / Agent Activity is expanded — the result can be
  // lengthy (full reconciliation table, checklist) and squeezes the agent log
  // if both render together. Both can also be collapsed if the user just wants
  // to see the metadata. Default to showing the result.
  const [expandedSection, setExpandedSection] = useState<"result" | "agent" | null>("result");
  const isResultExpanded = expandedSection === "result";
  const isAgentExpanded = expandedSection === "agent";

  if (!task) return null;

  const statusColor = getTaskStatusColor(task.status);
  const statusLabel = normalizeTaskStatus(task.status);
  const StatusIcon = getStatusIcon(task.status);
  const result = parseResult(task.result);
  const agentMessages = parseAgentMessages(task.agentMessages);

  return (
    <Dialog open={isOpen} onOpenChange={(_e, data) => !data.open && onDismiss()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <DialogTitle
            className={styles.dialogTitle}
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={onDismiss}
              />
            }
          >
            <div className={styles.header}>
              <Bot24Regular className={styles.headerIcon} />
              {task.name}
            </div>
          </DialogTitle>

          <DialogContent className={styles.dialogContent}>
            {/* Status and badges */}
            <div className={styles.badges}>
              <Badge
                appearance="filled"
                style={{ backgroundColor: statusColor, color: "white" }}
                icon={<StatusIcon />}
              >
                {statusLabel}
              </Badge>
              {task.outputType && (
                <Badge appearance="outline">{task.outputType}</Badge>
              )}
              {task.taskType && (
                <Badge appearance="tint">{task.taskType}</Badge>
              )}
            </div>

            <Divider />

            {/* Result */}
            <div className={styles.section}>
              <div
                className={styles.agentActivityHeader}
                onClick={() => setExpandedSection(isResultExpanded ? null : "result")}
              >
                {isResultExpanded ? <ChevronDown24Regular /> : <ChevronRight24Regular />}
                <Text className={typography.sectionLabel}>Result</Text>
              </div>
              {isResultExpanded && (
                result ? (
                  <div className={styles.resultContainer}>
                    {/* Failure shape wins before the output-type-specific
                        renderers — a failed task should always show the
                        user-friendly error view regardless of its output type. */}
                    {isErrorResult(result) ? (
                      <ErrorResultView result={result} styles={styles} />
                    ) : (
                      <>
                        {isReconciliationResult(result, task.outputType) && (
                          <ReconciliationResultView agentMessages={agentMessages} styles={styles} />
                        )}
                        {isChecklistResult(result, task.outputType) && (
                          <ChecklistResultView result={result} styles={styles} />
                        )}
                        {isFreeformResult(task.outputType) && (
                          // Reuses the "last assistant message rendered as markdown"
                          // view from the reconciliation path — same behaviour, just
                          // applied to freeform tasks too.
                          <ReconciliationResultView agentMessages={agentMessages} styles={styles} />
                        )}
                        {isDocumentResult(result, task.outputType) && (
                          <DocumentResultView result={result} styles={styles} />
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <Text className={styles.noResult}>
                    No result yet. Task is pending processing.
                  </Text>
                )
              )}
            </div>

            {/* Agent Activity */}
            {agentMessages.length > 0 && (
              <>
                <Divider />
                <div className={styles.agentActivitySection}>
                  <div
                    className={styles.agentActivityHeader}
                    onClick={() => setExpandedSection(isAgentExpanded ? null : "agent")}
                  >
                    {isAgentExpanded ? <ChevronDown24Regular /> : <ChevronRight24Regular />}
                    <Text className={typography.sectionLabel}>Agent Activity ({agentMessages.length})</Text>
                  </div>
                  {isAgentExpanded && (
                    <div className={styles.agentActivityList}>
                      {agentMessages.map((msg, index) => (
                        <div key={index} className={styles.agentMessage}>
                          {msg.Role === "assistant" && msg.Content && (
                            <>
                              <div className={`${typography.caption} ${styles.agentMessageLabel}`}>
                                <Chat24Regular />
                                <Text weight="semibold">Assistant</Text>
                              </div>
                              <div className={styles.responseText}>
                                <Markdown remarkPlugins={[remarkGfm]}>{msg.Content}</Markdown>
                              </div>
                            </>
                          )}
                          {msg.Role === "assistant" && msg.ToolName && (
                            <>
                              <div className={`${typography.caption} ${styles.agentMessageLabel}`}>
                                <Wrench24Regular />
                                <Text weight="semibold">Tool Call: {formatToolName(msg.ToolName)}</Text>
                              </div>
                              {msg.ToolArguments && (
                                <CollapsibleToolBlock content={msg.ToolArguments} styles={styles} />
                              )}
                            </>
                          )}
                          {msg.Role === "tool" && (
                            <>
                              <div className={`${typography.caption} ${styles.agentMessageLabel}`}>
                                <Wrench24Regular />
                                <Text weight="semibold">Tool Result{msg.ToolName ? `: ${formatToolName(msg.ToolName)}` : ""}</Text>
                              </div>
                              {msg.ToolResult && (
                                <CollapsibleToolBlock content={msg.ToolResult} styles={styles} />
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            <Divider />

            {/* Timestamps */}
            <div className={`${typography.caption} ${styles.timestamps}`}>
              {task.createdAt && (
                <div className={styles.timestampItem}>
                  <Clock24Regular />
                  <Text>Created: {formatDateTime(task.createdAt)}</Text>
                </div>
              )}
              {task.updatedAt && (
                <div className={styles.timestampItem}>
                  <Clock24Regular />
                  <Text>Updated: {formatDateTime(task.updatedAt)}</Text>
                </div>
              )}
            </div>

            {/* Created by */}
            {task.createdByName && (
              <Text className={typography.caption}>
                Created by: {task.createdByName}
                {task.createdByEmail && ` (${task.createdByEmail})`}
              </Text>
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default TaskDetailDialog;
